<template>
  <div class="editable-text" :class="{ '-single-line': singleLine }">
    <editor-content
      v-if="editingText"
      class="editable-text__input"
      :editor="(editor as Editor)"
    />
    <button
      v-else
      type="button"
      class="button-transparent editable-text__displayed-text"
      @dblclick.stop="startEditingText($event)"
      :title="displayedText"
    >
      <slot>
        <span v-if="displayedText" :class="{ [props.placeholderClass]: displayedText != text }">
          {{ displayedText }}
        </span>
      </slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch, computed, useId } from 'vue';
import { Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import {
  Editor,
  EditorContent,
  useEditor
} from "@tiptap/vue-3";
import type { EditorEvents, EditorOptions } from "@tiptap/core";

const props = withDefaults(defineProps<{
  id?: string;
  editable?: boolean;
  displayedText: string;
  placeholderClass?: string;
  singleLine?: boolean;
}>(), {
  editable: true,
  singleLine: true,
  placeholderClass: 'placeholder-text',
});
const text = defineModel<string>("text", { required: true });
const emit = defineEmits<{
  (event: "enter", text: string): void;
  (event: "focus"): void;
  (event: "blur", blurEvent: FocusEvent): void;
}>();


const uniqueId = useId();

const editingText = ref(false);

async function startEditingText(event?: MouseEvent) {
  if (!props.editable) return;
  editingText.value = true;
  await nextTick();
  await nextTick();
  
  if (editor.value) {
    editor.value.chain().focus().selectAll().run();
  }
}
function stopEditingText() {
  editingText.value = false;
}


// =======================
// EDITOR

const EnterListener = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        emit("enter", getText());
        stopEditingText();
        return true;
      }
    };
  }
});

const OneLineDocument = Document.extend({
  content: "text*"
});
const minimalExtensions = [OneLineDocument, Text, EnterListener];

const editorOptions = computed((): Partial<EditorOptions> => {
  return {
    autofocus: false,
    editorProps: {
      attributes: {
        id: props.id ?? uniqueId
      }
    },
    parseOptions: {
      preserveWhitespace: true
    },
    content: text.value,
    extensions: minimalExtensions,
    onFocus: () => emit("focus"),
    onBlur: ({ event }) => onBlur(event),
    onUpdate: onUpdate
  };
});

const editor = useEditor(editorOptions.value);

watch(
  text,
  (val: string) => {
    updateContent(val);
  },
  { immediate: true }
);

function updateContent(val: string) {
  if (editor.value == null) return;

  if (editor.value.getText() !== val) {
    editor.value.chain().setContent(val, { parseOptions: { preserveWhitespace: true }}).run();
  }
}

function getText() {
  if (editor.value == null) return "";
  return editor.value.getText();
}

function onBlur(event: FocusEvent) {
  stopEditingText();
  emit("blur", event);
}

function onUpdate(editorProps: EditorEvents["update"]) {
  const updatedText = getText();
  if (updatedText === text.value) return;
  text.value = updatedText;
}

defineExpose({
  startEditingText,
  stopEditingText,
});
</script>

<style lang="scss">
.editable-text {
  overflow: hidden;

  &__input {
    appearance: none;
    display: block;
    border: none;
    padding: 0;
    outline: none;
    background-color: transparent;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;

    .tiptap {
      outline: none;
    }
  }
  &__displayed-text,
  &__input {
    padding: 0.25em 0;
    width: 100%;
    overflow: hidden;
  }
  .-single-line &__displayed-text,
  .-single-line &__input {
    white-space: nowrap;
    text-overflow: ellipsis;

    .tiptap {
      white-space: nowrap !important;
    }
  }
}
</style>
